import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@stackje/shared-components/ui/button";
import {
  ClearableInput,
  ClearableTextarea,
} from "@stackje/shared-components/ui/clearable-fields";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@stackje/shared-components/ui/dialog";
import { Label } from "@stackje/shared-components/ui/label";
import type { SelectedImageData } from "@/components/shared/ImageEditor";
import { FormImageUploader } from "@/components/shared/FormImageUploader";
import { UnsavedChangesConfirmDialog } from "@/components/shared/UnsavedChangesConfirmDialog";

// Zod schema for image validation
const imageSchema = z.object({
  image: z.object({
    src: z.string().min(1, "Image is required"),
    alt: z.string().optional(),
  }),
  caption: z.string().optional(),
});

type ImageFormData = z.infer<typeof imageSchema>;

interface ImageEditFormDialogContentProps {
  isEditing: boolean;
  initialData?: ImageFormData;
  onSubmit: (data: ImageFormData) => void;
  onClose?: () => void;
  editingImageIndex?: number | null; // Optional, for unique name/id generation
  imagesLength?: number; // Optional, for unique name/id generation
}

export function ImageEditFormDialogContent({
  isEditing,
  initialData,
  onSubmit,
  onClose,
  editingImageIndex,
  imagesLength,
}: ImageEditFormDialogContentProps) {
  const form = useForm<ImageFormData>({
    resolver: zodResolver(imageSchema),
    defaultValues: {
      image: {
        src: initialData?.image.src || "",
        alt: initialData?.image.alt || "",
      },
      caption: initialData?.caption || "",
    },
    mode: "onChange",
  });

  // Local state for image since it's not a simple form field
  const [imageValue, setImageValue] = useState<SelectedImageData | null>(
    initialData?.image.src ? { fullUrl: initialData.image.src, previewUrl: initialData.image.src } : null
  );

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const isDirty = form.formState.isDirty || 
    (imageValue?.fullUrl !== initialData?.image.src && imageValue?.fullUrl !== "");

  const handleSubmit = form.handleSubmit((data) => {
    // Transform image data if needed
    const submitData: ImageFormData = {
      image: {
        src: imageValue?.fullUrl || data.image.src,
        alt: data.image.alt || data.caption || `Image ${(editingImageIndex ?? imagesLength ?? 0) + 1}`,
      },
      caption: data.caption,
    };
    onSubmit(submitData);
    form.reset();
  });

  // Update form when image is selected
  useEffect(() => {
    if (imageValue?.fullUrl) {
      form.setValue("image.src", imageValue.fullUrl, { shouldValidate: true });
    }
  }, [imageValue, form]);

  // Reset form when editing different image (initialData changes) or when switching between add/edit modes
  useEffect(() => {
    if (initialData) {
      form.reset({
        image: {
          src: initialData.image.src,
          alt: initialData.image.alt || "",
        },
        caption: initialData.caption || "",
      });
      // Also update the image preview
      setImageValue(
        initialData.image.src ? { fullUrl: initialData.image.src, previewUrl: initialData.image.src } : null
      );
    } else {
      // When adding new image, reset form
      form.reset({
        image: { src: "", alt: "" },
        caption: "",
      });
      setImageValue(null);
    }
  }, [initialData, isEditing, form]);

  const handleClose = () => {
    console.log("isDirty", isDirty);
    if (isDirty) {
      setShowConfirmDialog(true);
    } else {
      onClose?.();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose?.();
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <DialogContent 
        className="w-full h-[100dvh] sm:max-w-[425px] sm:w-[90vw] sm:h-auto sm:max-h-[85vh] overflow-hidden flex flex-col" 
        onPointerDownOutside={(e) => {
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          e.preventDefault();
        }}
        onClose={handleClose}
      >
      <DialogHeader className="flex-shrink-0">
        <DialogTitle>{isEditing ? "Edit image" : "Add an image"}</DialogTitle>
      </DialogHeader>
      <div className="flex flex-col gap-4 p-4 flex-1 overflow-y-auto min-w-0">
        <div className="flex flex-col mb-2">
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          <FormImageUploader
            label="Image"
            currentImage={imageValue}
            onImageChange={setImageValue}
            allowFreeCrop={true}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="imageAltDialog">Alt Text</Label>
          <Controller
            name="image.alt"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="imageAltDialog"
                name={`imageAltDialog${isEditing ? editingImageIndex : imagesLength}`}
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Enter image alt text for accessibility"
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-2 min-w-0">
          <Label htmlFor="imageCaptionDialog">Caption</Label>
          <Controller
            name="caption"
            control={form.control}
            render={({ field }) => (
              <ClearableTextarea
                id="imageCaptionDialog"
                name={`imageCaptionDialog${isEditing ? editingImageIndex : imagesLength}`}
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Enter image caption (optional)"
                rows={2}
              />
            )}
          />
        </div>
      </div>
      <DialogFooter className="flex-shrink-0 sticky bottom-0 bg-background border-t pt-4 mt-auto">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={form.formState.isSubmitting || !form.formState.isValid}
          className="w-full"
        >
          {isEditing ? "Save changes" : "Add image"}
        </Button>
      </DialogFooter>
    </DialogContent>

    <UnsavedChangesConfirmDialog
      open={showConfirmDialog}
      onOpenChange={setShowConfirmDialog}
      onConfirm={handleConfirmClose}
      onCancel={handleCancelClose}
    />
    </>
  );
}